import { RouteFactory } from "@app/routePaths";
import { Section, Tag } from "@app/types/Cue";
import DefaultSectionLayout from "@pages/Section/layouts/DefaultSectionLayout";
import ThrivePage from "@pages/Section/layouts/ThrivePage/ThrivePage";
import AseanBusinessVertical from "@pages/Section/layouts/Verticals/AseanBusiness/AseanBusinessVertical";
import GlobalEnterpriseVertical from "@pages/Section/layouts/Verticals/GlobalEnterprise/GlobalEnterpriseVertical";
import SMEVertical from "@pages/Section/layouts/Verticals/SME/SMEVertical";
import VerticalsLatestPage from "@pages/Section/layouts/Verticals/VerticalsLatestPage";
import { TagType } from "@pages/Section/types";
import { FeedSection, FeedSectionEntity } from "@pages/Section/utils/types";
import {
  getEnvironment,
  sectionNavigationItems,
  VerticalName,
} from "@util/constant";
import { getEnvironmentDomain, slugToText } from "@util/helpers";

export const LayoutFactory = (urlPath: string) => {
  switch (true) {
    case urlPath === RouteFactory.globalEnterpriseLatest:
    case urlPath === RouteFactory.aseanBusinessLatest:
    case urlPath === RouteFactory.sgsmeLatest:
      return VerticalsLatestPage;

    case urlPath.startsWith(RouteFactory.thrive):
      return ThrivePage;

    default:
      return DefaultSectionLayout;
  }
};

export const VerticalLayoutFactory = (verticalName: VerticalName) => {
  switch (verticalName) {
    case "asean":
      return AseanBusinessVertical;
    case "sme":
      return SMEVertical;
    case "ge":
      return GlobalEnterpriseVertical;
  }
};

/**
 * Helper function to format a text that is suited for Ads Team's requirements.
 * Examples of keyword text shown on helpers.test.tsx
 * @param {string} text The text that needs to be formatted.
 * @returns {string}
 */
export const getFormattedTextForAds = (text: string): string => {
  return text
    .replaceAll(/&amp;|&/g, "and")
    .replaceAll(/[;/\\]/g, "")
    .toLowerCase();
};

/**
 * Helper function to get the path of Section or Tag
 * @param entity Section or Tag
 * @returns {string|undefined}
 */
export const getTermPath = (entity?: Section | Tag): string | undefined => {
  if (!entity) return;

  if (isEntityTagType(entity)) {
    if (!entity.urlPath) return;
    return entity.urlPath;
  }

  if (isEntitySectionType(entity)) {
    return entity.uniqueName.replaceAll("_", "/");
  }
};

/**
 * Helper function to get the tag object from list of tags.
 * @param tags List of tags.
 * @param keywordUri The uri of keyword you wanna search.
 * @returns
 */
export const getTagObject = (tags: Tag[] = [], keywordUrlpath: string): Tag => {
  const manuallyGeneratedTag: Tag = {
    name: slugToText(keywordUrlpath.replace("/keywords/", "")),
    uri: keywordUrlpath,
    aliases: [],
    fields: [],
    id: "1",
    parent: null,
    sections: [],
    type: TagType.Keyword,
    urlPath: keywordUrlpath,
    urlPathHistory: [],
  };

  const tagFound =
    tags.find((tag) => tag.urlPath === keywordUrlpath) || manuallyGeneratedTag;
  const idFound = tagFound?.fields.find(({ key }) => key === "id")?.value;
  const sanitizedTag: Tag = { ...tagFound, id: idFound || "1" };

  return sanitizedTag;
};

/**
 * Helper finction to get the section object from list of sections.
 * @param sections List of sections.
 * @param sectionUniqueName The unique name of section you wanna search.
 * @returns
 */
export const getSectionObject = (
  sections: Section[] = [],
  sectionUniqueName: string
) => {
  return sections.find(({ uniqueName }) => uniqueName === sectionUniqueName);
};

/**
 * Helper function to check if entity is Section
 * @param entity The entity to check.
 * @returns {boolean}
 */
export const isEntitySectionType = (
  entity: Section | Tag
): entity is Section => {
  return (
    typeof entity === "object" && entity !== null && "uniqueName" in entity
  );
};

/**
 * Helper function to check if entity is Tag
 * @param entity The entity to check.
 * @returns {boolean}
 */
export const isEntityTagType = (entity: Section | Tag): entity is Tag => {
  return typeof entity === "object" && entity !== null && "urlPath" in entity;
};

/**
 * Helper function to get the section heading class.
 * @param path The url path.
 * @returns {string}
 */
export const getSectionHeadingClass = (path: string): string => {
  if (!path) return "";

  if (path.startsWith(RouteFactory.thrive)) {
    return "pb-3 pt-6 thrive-past-event-title stroke-gray-850 stroke-1 font-interBold text-6xl font-bold leading-[2.5rem] !text-white text-shadow-thrive-2 md:text-8xl";
  }

  if (path.startsWith(RouteFactory.newsletters)) return "uppercase pb-0";

  return "uppercase";
};

/**
 * Helper function to get the section heading text.
 * @param path The url path.
 * @param text The default text.
 * @returns {string}
 */
export const getSectionHeadingText = (path: string, text: string): string => {
  if (path?.startsWith(RouteFactory.thrive)) {
    return "Let's talk Career, Money and Life.";
  }

  return text;
};

/**
 * Helper function to truncate section heading text.
 * @param isScreenXL Flag to check extra large screens.
 * @param isScreenLG Flag to check larger screens.
 * @returns {number}
 */
export const getTruncateTitleLength = (
  isScreenLG: boolean,
  isScreenXL: boolean
) => {
  if (isScreenXL) {
    return 40;
  } else if (isScreenLG) {
    return 23;
  } else {
    return 30;
  }
};

/**
 * Helper function to get the section object for navigation and parent category.
 * @param relativePath The relative path of the section.
 * @param sectionEntity The section entity object.
 * @returns {FeedSection}
 */
export const sanitizeSectionObject = (
  relativePath: string,
  sectionEntity: FeedSectionEntity
): FeedSection => {
  const mainCategoryEntity = sectionEntity.sections[0];
  const _relativePath = relativePath.replaceAll("/latest", "");
  const [parentCategory, childCategory] = _relativePath.substring(1).split("/");
  const mainCategoryObj = sectionNavigationItems[_relativePath.substring(1)];
  const parentCategoryObj =
    childCategory && parentCategory
      ? sectionNavigationItems[parentCategory]
      : null;

  const env = getEnvironment();
  const domain = getEnvironmentDomain(env);

  const sectionObj: FeedSection = {
    name: mainCategoryObj?.label || "",
    uniqueName: mainCategoryEntity?.uniqueName || "",
    urlPath: mainCategoryEntity.urlPath || "",
    relativePath: relativePath,
    parent: parentCategoryObj
      ? {
          name: parentCategoryObj.label,
          uniqueName: parentCategoryObj.key.replaceAll("/", "_"),
          urlPath: `${domain}${parentCategoryObj.link}`,
          relativePath: parentCategoryObj.link,
        }
      : undefined,
  };

  return JSON.parse(JSON.stringify(sectionObj));
};
